import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom'
import dateFormat from 'dateformat'
import $ from 'jquery'

import SearchBox from '../components/psc/lib/SearchBox'
import HackedSelectFilterAttributeMulti from '../components/psc/lib/HackedSelectFilterAttributeMulti'
import GncDataTable from '../components/psc/lib/GncDataTable'
import Loading from '../components/psc/lib/Loading'

import useAuthentication from '../hooks/useAuthentication';
import useUserManagement from '../hooks/useUserManagement';
import useTableSlots from '../hooks/useTableSlots';
import useProjectContents from '../hooks/useProjectContents';

const ActionItems = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const { projectID } = useParams()
    
    const pscState = useSelector(state => state.pscState.value)
    const project = pscState.project
    const isLoading = pscState.isLoading

    const { currentUser } = useAuthentication();

    const [selectedMeetingNames, setSelectedMeetingNames] = useState([])
    const [selectedMeetingTypes, setSelectedMeetingTypes] = useState([])
    const [selectedMeetingStatuses, setSelectedMeetingStatuses] = useState([])
    
    const table = useRef(null)
    const localStorageOrderKey = 'tblActionItemsOrder'
    const localStorageStatusKey = 'tblActionItemsStatus'
    const defaultOrder = [[ 2, 'desc' ]]

    const {
        userHasProjectAccessLevel,
        getUserFromID,
        isUserManagementInitialized
    } = useUserManagement()

    const {
        isActionItemsLoaded,
        isActionItemSourcesLoaded,
        actionItems,
        actionItemSources,
    } = useProjectContents()
    
    const {
        dateSlot,
        filePathSlot,
        linkSlot,
        ownerSlot
    } = useTableSlots()

    useEffect(() => {
        // Query string params take precedence over what's already stored
        // source , status
        if (location.search) {
            const query = new URLSearchParams(location.search)
            if( query.has('source') ) {
                const key = 'actionItemsSourceFilter_' + projectID
                const value = [{
                    label: query.get('source'),
                    value: query.get('source')
                }]
                localStorage[key] = JSON.stringify(value)
            }
            if( query.has('status') ) {
                const key = 'actionItemsStatusFilter_' + projectID
                const value = [{
                    label: query.get('status'),
                    value: query.get('status')
                }]
                localStorage[key] = JSON.stringify(value)
            }
        }
    }, [location.search])

    useEffect(() => {
        $.fn.dataTable.ext.search = [
            function( settings, data, dataIndex ) {
                const meetingName = data[0]
                const meetingType = data[1]
                const meetingStatus = data[9]
                
                const hasMeetingName = selectedMeetingNames?.some(element => element.value === meetingName)
                const hasMeetingType = selectedMeetingTypes?.some(element => element.value === meetingType)
                const hasMeetingStatus = selectedMeetingStatuses?.some(element => element.value === meetingStatus)

                if (selectedMeetingNames?.length > 0 && !hasMeetingName)
                    return false

                if (selectedMeetingTypes?.length > 0 && !hasMeetingType)
                    return false

                if (selectedMeetingStatuses?.length > 0 && !hasMeetingStatus)
                    return false
                
                return true
            }
        ]
        return () => {
            // Clear the filtering
            $.fn.dataTable.ext.search = []
        }
    }, [selectedMeetingNames, selectedMeetingTypes, selectedMeetingStatuses])

    useEffect(() => {
        $('.dataTable').DataTable()?.search()
        $('.dataTable').DataTable()?.draw('')
    }, [selectedMeetingNames, selectedMeetingTypes, selectedMeetingStatuses])

    const columns = [
        { data: 'action_item_source.name' },
        { data: 'type' },
        { data: 'raised_date' },
        { data: 'action_item_number' },
        { data: 'title' },
        { data: 'origin' },
        { data: 'description', visible: false, searchable: false },
        { data: 'owner_user_id' },
        { data: 'due_date' },
        { data: 'status' },
        { data: 'close_reference', visible: false, searchable: false },
        { data: 'comments', visible: false, searchable: false }
    ]

    const tableHead = (
        <tr>
            <th>Meeting</th>
            <th title="Internal / External" style={{whiteSpace: 'nowrap', textAlign: 'center', width: '35px'}}>I/E</th>
            <th>Raised</th>
            <th>AI #</th>
            <th>Title</th>
            <th>Origin</th>
            <th>Description</th>
            <th>Owner</th>
            <th>Due</th>
            <th>Status</th>
            <th>Close out Reference</th>
            <th>Comments</th>
        </tr>
    )
    
    const tableSlots = {
        1: (data, type, row) => {
            if (type === 'filter') return data
            if (data === 'External') return (<div style={{textAlign: 'center'}} title="External">E</div>)
            if (data === 'Internal') return (<div style={{textAlign: 'center'}} title="Internal">I</div>)
            return data
        },
        2: dateSlot,
        3: (data, type, row) => {
            if (type !== 'display') return data
            const url = `/portfolio/${row.project_id}/action_items/${row.id}`
            const name = `AI-${String(data).padStart(2, '0')}`
            return <a href={url} onClick={(e) => { e.preventDefault(); navigate(url); }}>{name}</a>
        },
        4: (data, type, row) => linkSlot(data, type, row, `/portfolio/${row.project_id}/action_items/${row.id}`),
        7: (data, type, row) => {
            if (parseInt(data, 10) > 0) {
                const user = getUserFromID(data)
                if (type !== 'display') return user.label
                return <span data-tooltip-id="gnctooltip" data-tooltip-content={user.label}>{user.display_name}</span>
            }
            return row.owner_freetext
        },
        8: (data, type, row) => {
            if (type === 'sort') return data
            if (data === null || data === '') return row.due_date_freetext
            const value = isNaN(Date.parse(data)) ? row.due_date_freetext : dateFormat(Date.parse(data), 'dd-mm-yyyy')
            return <span style={{whiteSpace: 'nowrap'}}>{value}</span>
        }
    }

    let actions
    if( project && userHasProjectAccessLevel(currentUser, project.id, 'MEMBER') ) {
        actions = (
            <div className="pre-table-buttons">
                <Link type="button" className="btn btn-mr btn-primary" to={`/portfolio/${project.id}/action_items/new`}>New Action Item </Link>
                <Link to={`/portfolio/${project.id}/action_item_sources/new`}>New Meeting</Link>
                <Link to={`/portfolio/${project.id}/action_item_sources`}>Manage Meetings</Link>
            </div>
        )
    }

    const types = {
        single_select_values: [
            {
                value: 'External'
            },
            {
                value: 'Internal'
            }
        ]
    }
    const statuses = {
        single_select_values: [
            {
                value: 'Open'
            },
            {
                value: 'Closed'
            }
        ]
    }

    const meetings = {
        single_select_values: actionItemSources.map(item => {
            return {
                value: item.name,
                label: item.name
            }})
    }

    const showLoading = isLoading || !isUserManagementInitialized || !isActionItemsLoaded || !isActionItemSourcesLoaded
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Action Items</h2>

                            {actions}
                            
                        </div>
                        <div className="ibox-content">
                            <div>
                                <div className="row">
                                    <HackedSelectFilterAttributeMulti
                                        attribute={meetings}
                                        columnIndex="0"
                                        localStorageKey={`actionItemsSourceFilter_${projectID}`}
                                        placeholder="Select Meeting..."
                                        onChange={(value) => setSelectedMeetingNames(value)}
                                    />

                                    <HackedSelectFilterAttributeMulti
                                        attribute={types}
                                        columnIndex="1"
                                        localStorageKey={`actionItemsTypeFilter_${projectID}`}
                                        placeholder="Select Meeting Type..."
                                        onChange={(value) => setSelectedMeetingTypes(value)}
                                    />
                                </div>

                                <div className="row">
                                    <SearchBox tableRef={table} />

                                    <HackedSelectFilterAttributeMulti
                                        tableRef={table}
                                        attribute={statuses}
                                        columnIndex="9"
                                        localStorageKey={`actionItemsStatusFilter_${projectID}`}
                                        placeholder="Select Status..."
                                        onChange={(value) => setSelectedMeetingStatuses(value)}
                                    />
                                </div>
                            </div>
                            
                            { showLoading ? <Loading /> : (
                                <GncDataTable
                                    tableRef={table}
                                    data={actionItems}
                                    defaultOrder={defaultOrder}
                                    localStorageOrderKey={localStorageOrderKey}
                                    columns={columns}
                                    tableSlots={tableSlots}
                                    tableHead={tableHead}
                                    tableFoot={tableHead}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ActionItems
